import { useContext, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "react-query";
import { MdOutlineImage } from "react-icons/md";
import { FiPlus, FiTrash2, FiChevronUp, FiChevronDown, FiEdit2, FiCheckCircle } from "react-icons/fi";
import { AuthContext } from "../../../Context/AuthContext";
import useGetBanners from "../../../marketplace_admin/libs/useGetBanners";
import useDeleteBanner from "../../../marketplace_admin/libs/useDeleteBanner";
import PageHeader from "../../components/PageHeader";
import EmptyState from "../../components/EmptyState";
import ConfirmModal from "../../components/ConfirmModal";
import Spinner from "../../../Spinner/Spinner";

const emptyEdit = { linkUrl: "", linkTarget: "_self", altText: "", enabled: true };

const AdminBannersPage = () => {
  const { token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useGetBanners();
  const { deleteBanner, isLoading: deleteLoading } = useDeleteBanner();

  const banners = data?.banners || [];
  const [uploadOpen, setUploadOpen] = useState(false);
  const [images, setImages] = useState([]);
  const [deleteId, setDeleteId] = useState(null);
  const [editBanner, setEditBanner] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);

  const uploadMutation = useMutation(
    async () => {
      const formData = new FormData();
      images.forEach((image) => formData.append("images", image));
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/banners/create/banner`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("banners");
        toast.success("Banner uploaded");
        setUploadOpen(false);
        setImages([]);
      },
      onError: () => toast.error("Upload failed"),
    }
  );

  const updateMutation = useMutation(
    async ({ id, payload }) => {
      const response = await axios.patch(
        `${process.env.REACT_APP_SERVER_URL}/banners/${id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("banners");
        toast.success("Banner updated");
        setEditBanner(null);
      },
      onError: () => toast.error("Update failed"),
    }
  );

  const enableAllMutation = useMutation(
    async () => {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/banners/enable-all`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("banners");
        toast.success("All banners enabled");
      },
      onError: () => toast.error("Could not enable all banners"),
    }
  );

  const reorderMutation = useMutation(
    async (bannerIds) => {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/banners/reorder`,
        { bannerIds },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: () => queryClient.invalidateQueries("banners"),
      onError: () => toast.error("Could not reorder banners"),
    }
  );

  const handleDelete = async () => {
    try {
      await deleteBanner(deleteId);
      toast.success("Banner deleted");
      setDeleteId(null);
    } catch {
      toast.error("Could not delete banner");
    }
  };

  const openEdit = (banner) => {
    setEditBanner(banner);
    setEditForm({
      linkUrl: banner.linkUrl || "",
      linkTarget: banner.linkTarget || "_self",
      altText: banner.altText || "",
      enabled: banner.enabled !== false,
    });
  };

  const handleSaveEdit = () => {
    if (!editBanner) return;
    updateMutation.mutate({ id: editBanner._id, payload: editForm });
  };

  const moveBanner = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= banners.length) return;
    const reordered = [...banners];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    reorderMutation.mutate(reordered.map((b) => b._id));
  };

  const toggleEnabled = (banner) => {
    updateMutation.mutate({
      id: banner._id,
      payload: { enabled: banner.enabled === false },
    });
  };

  const allEnabled = banners.length > 0 && banners.every((banner) => banner.enabled !== false);

  return (
    <>
      <PageHeader
        title="Banners"
        subtitle="Homepage carousel — reorder, link, and enable or disable slides"
        icon={MdOutlineImage}
        actions={
          <>
            {banners.length > 0 && (
              <button
                type="button"
                className="dashboard-btn dashboard-btn--secondary"
                disabled={allEnabled || enableAllMutation.isLoading}
                onClick={() => enableAllMutation.mutate()}
              >
                <FiCheckCircle aria-hidden="true" />
                {enableAllMutation.isLoading ? "Enabling…" : "Enable all"}
              </button>
            )}
            <button type="button" className="dashboard-btn dashboard-btn--primary" onClick={() => setUploadOpen(true)}>
              <FiPlus aria-hidden="true" /> Upload banner
            </button>
          </>
        }
      />

      {isLoading && <Spinner />}
      {isError && (
        <div className="dashboard-card">
          <p style={{ color: "var(--dash-danger)", margin: 0 }}>Could not load banners.</p>
        </div>
      )}

      {!isLoading && !isError && banners.length === 0 && (
        <EmptyState
          icon={MdOutlineImage}
          title="No banners yet"
          message="Upload images to display on the marketplace homepage."
          actionLabel="Upload banner"
          onAction={() => setUploadOpen(true)}
        />
      )}

      <div className="admin-banner-grid">
        {banners.map((banner, index) => (
          <article
            key={banner._id}
            className={`dashboard-card admin-banner-card${banner.enabled === false ? " admin-banner-card--disabled" : ""}`}
          >
            <div className="admin-banner-card__order">
              <button
                type="button"
                className="dashboard-btn dashboard-btn--icon"
                disabled={index === 0 || reorderMutation.isLoading}
                onClick={() => moveBanner(index, -1)}
                aria-label="Move up"
              >
                <FiChevronUp />
              </button>
              <span>{index + 1}</span>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--icon"
                disabled={index === banners.length - 1 || reorderMutation.isLoading}
                onClick={() => moveBanner(index, 1)}
                aria-label="Move down"
              >
                <FiChevronDown />
              </button>
            </div>
            <img src={banner.imageUrl} alt={banner.altText || "Banner preview"} />
            <div className="admin-banner-card__meta">
              {banner.linkUrl ? (
                <a href={banner.linkUrl} target="_blank" rel="noopener noreferrer" className="dashboard-link">
                  {banner.linkUrl}
                </a>
              ) : (
                <span className="dashboard-form-hint">No link</span>
              )}
              <label className="admin-banner-toggle">
                <input
                  type="checkbox"
                  checked={banner.enabled !== false}
                  onChange={() => toggleEnabled(banner)}
                  disabled={updateMutation.isLoading}
                />
                <span>{banner.enabled === false ? "Disabled" : "Enabled"}</span>
              </label>
            </div>
            <div className="admin-banner-card__footer">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => openEdit(banner)}>
                <FiEdit2 aria-hidden="true" /> Edit
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--danger"
                onClick={() => setDeleteId(banner._id)}
              >
                <FiTrash2 aria-hidden="true" /> Delete
              </button>
            </div>
          </article>
        ))}
      </div>

      {uploadOpen && (
        <div className="dashboard-modal-backdrop" onClick={() => setUploadOpen(false)} role="presentation">
          <div className="dashboard-modal admin-banner-upload" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Upload banner</h3>
            <p style={{ marginTop: 0, color: "var(--dash-muted)", fontSize: 14 }}>
              Use wide landscape images for best results on the homepage carousel.
            </p>
            {images.length > 0 ? (
              <div className="admin-banner-upload__previews">
                {images.map((file, idx) => (
                  <img key={`${file.name}-${idx}`} src={URL.createObjectURL(file)} alt="" />
                ))}
              </div>
            ) : (
              <label className="dashboard-image-upload-slot admin-banner-upload__slot">
                <FiPlus aria-hidden="true" />
                <span>Choose images</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => setImages(Array.from(e.target.files || []))}
                />
              </label>
            )}
            <div className="dashboard-modal__actions">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setUploadOpen(false)}>
                Cancel
              </button>
              {images.length > 0 && (
                <>
                  <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setImages([])}>
                    Clear
                  </button>
                  <button
                    type="button"
                    className="dashboard-btn dashboard-btn--primary"
                    disabled={uploadMutation.isLoading}
                    onClick={() => uploadMutation.mutate()}
                  >
                    {uploadMutation.isLoading ? "Uploading…" : "Upload"}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {editBanner && (
        <div className="dashboard-modal-backdrop" onClick={() => setEditBanner(null)} role="presentation">
          <div className="dashboard-modal admin-banner-edit" onClick={(e) => e.stopPropagation()} role="dialog">
            <h3>Edit banner</h3>
            <label className="dashboard-form-field">
              <span>Link URL (optional)</span>
              <input
                type="url"
                value={editForm.linkUrl}
                onChange={(e) => setEditForm((f) => ({ ...f, linkUrl: e.target.value }))}
                placeholder="https:// or /market/..."
              />
            </label>
            <label className="dashboard-form-field">
              <span>Link opens in</span>
              <select
                value={editForm.linkTarget}
                onChange={(e) => setEditForm((f) => ({ ...f, linkTarget: e.target.value }))}
              >
                <option value="_self">Same tab</option>
                <option value="_blank">New tab</option>
              </select>
            </label>
            <label className="dashboard-form-field">
              <span>Alt text</span>
              <input
                type="text"
                value={editForm.altText}
                onChange={(e) => setEditForm((f) => ({ ...f, altText: e.target.value }))}
                placeholder="Describe the banner for accessibility"
              />
            </label>
            <label className="admin-banner-toggle">
              <input
                type="checkbox"
                checked={editForm.enabled}
                onChange={(e) => setEditForm((f) => ({ ...f, enabled: e.target.checked }))}
              />
              <span>Show on homepage</span>
            </label>
            <div className="dashboard-modal__actions">
              <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={() => setEditBanner(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn--primary"
                disabled={updateMutation.isLoading}
                onClick={handleSaveEdit}
              >
                {updateMutation.isLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={Boolean(deleteId)}
        title="Delete banner?"
        message="This image will be removed from the marketplace homepage."
        confirmLabel="Delete"
        danger
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </>
  );
};

export default AdminBannersPage;
